import { useState } from "react";
import { Check, Copy, type LucideIcon } from "lucide-react";
import { cn } from "@wa/ui";
import { Card, CardHead, IconBtn } from "../../../ui";

/**
 * The shared furniture of the scanner's detail panels.
 *
 * Every panel on the scan page is the same card with the same label/value grid
 * inside it, so the card, the field and the copy button live here and each panel
 * file stays about the thing it shows.
 *
 * That one card is also where the scan detail rejoined the rest of the CRM. The
 * scans table below it had already moved to `crm-card` - no border, a 28px
 * radius, a diffuse shadow - while every panel above it was still a bordered
 * `rounded-2xl` box with a blur behind it. Same page, two eras. Changing `Panel`
 * moved all seven of them at once.
 */

export function Panel({
  icon: Icon,
  title,
  children,
  aside,
}: {
  icon: LucideIcon;
  title: string;
  children: React.ReactNode;
  aside?: React.ReactNode;
}) {
  return (
    <Card>
      <CardHead
        title={
          <span className="flex items-center gap-2">
            <Icon className="size-4 text-primary" /> {title}
          </span>
        }
        actions={aside}
      />
      {/* A `crm-card` carries no padding of its own, so every screen states what
          its contents need. Panels are dense, so this is tighter under the head
          than a list card is. In `index.css` rather than in Tailwind because the
          phone rule narrows this and the head above it by the same amount, and
          the two only stay aligned if one rule moves both. */}
      <div className="crm-scan-body">{children}</div>
    </Card>
  );
}

export function Field({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] font-medium text-muted-foreground">{label}</dt>
      <dd
        className={cn("truncate text-sm", mono && "font-mono text-xs")}
        title={typeof value === "string" ? value : undefined}
      >
        {value === null || value === "" ? "-" : value}
      </dd>
    </div>
  );
}

/** One-click copy, because these values exist to be pasted into an email. */
export function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <IconBtn
      small
      aria-label={label}
      title={label}
      // Borderless and smaller than a toolbar circle: it sits beside the value
      // it copies, and a ringed button there reads as a second field.
      className="size-7 border-0"
      onClick={() => {
        void navigator.clipboard?.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
      }}
    >
      {copied ? <Check className="size-3.5 text-[var(--color-success)]" /> : <Copy className="size-3.5" />}
    </IconBtn>
  );
}

/**
 * A length meter for title and description - the two fields every rewrite fixes.
 *
 * On the CRM's own success and warning colours rather than a hand-picked
 * emerald/amber pair. This is a verdict on a value, which is what a status pill
 * says everywhere else on the page, and it should not be a shade of its own.
 */
export function LengthMeter({ length, min, max }: { length: number; min: number; max: number }) {
  const ok = length >= min && length <= max;
  return (
    <span
      className="ml-1.5 font-mono text-[10px]"
      style={{ color: ok ? "var(--color-success)" : "var(--color-warning)" }}
    >
      {length}
    </span>
  );
}
